import { SocialLinks } from '../components/SocialLinks';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Landmark, BookOpen, Users, GraduationCap } from 'lucide-react';
import './FoundersCircle.css';

/*
  THLP Founders Circle — React port of the Claude Design "THLP Founders Circle.dc.html",
  EXPANDED to restore substance the first port dropped (recovered from the prior
  FoundersCirclePreview): Urgency stats, tier detail, the Partner Benefits matrix,
  and the Sustaining Founders toggle.
  Tier cards follow the team's reference: prominent "$X+" number in a colored block,
  the title pulled down above the body copy, and ALL tiers expanded side-by-side (no
  per-tier toggle) so they compare at a glance. The Urgency section carries a faint
  kids collage behind it (no bare solid block) with a forced two-line heading. The
  benefits matrix + Sustaining Founders live inside the dark section. Copy is vision
  tense + guide-accurate; guardrails hold. G5 "11,000/grade" softened pending confirm.
*/

const AC = '#14919f';
const NAV = [['About', '/about'], ['Our Model', '/model'], ['Research', '/research'], ['Founders Circle', '/founders-circle'], ['Contact', '/contact']];
const rise = (range) => ({ animation: 'riseIn .6s ease both', animationTimeline: 'view()', animationRange: range });
const eyebrow = (bg, color) => ({ display: 'inline-flex', alignItems: 'center', padding: '7px 16px', borderRadius: 999, background: bg, color, font: "700 11.5px/1 'Rubik'", letterSpacing: '.14em', textTransform: 'uppercase' });
const rubik = (extra = {}) => ({ fontFamily: "'Rubik', sans-serif", ...extra });

const PATHWAYS = [
  { t: 'State Leadership', d: 'Make history. Establish Delaware as the first state where book ownership is universal, ensuring every child has the foundation to succeed.' },
  { t: 'Philanthropic Leadership', d: 'Move boldly. Provide the catalytic investment that launches an evidence-based model and changes what’s possible for generations.' },
  { t: 'Business & Workforce Leaders', d: 'Invest early. Strengthen literacy, the foundational workforce skill, and build Delaware’s talent pipeline from the ground up.' },
  { t: 'Community & Education Partners', d: 'Build trust. Connect families to books, ensuring reach and participation in every neighborhood.' },
];

const SPONSORSHIP = [
  { t: 'Build Infrastructure', d: 'Name a warehouse zone, classroom, or workforce training space where books are curated, packed, and prepared for distribution.', Icon: Landmark },
  { t: 'Sponsor What Kids Learn', d: 'Sponsor all Financial Literacy, STEM, Diverse Heroes/History, or Social-Emotional Learning books distributed statewide in a given year.', Icon: BookOpen },
  { t: 'Reach Communities', d: 'Sponsor an entire school, district, or grade level, helping thousands of children build home libraries in communities that need it most.', Icon: Users },
  { t: 'Create Lasting Impact', d: 'Ensure continuity and scale in communities with the greatest literacy needs through multi-year Sustaining Founder commitments.', Icon: GraduationCap },
];

const URGENCY = [
  { stat: '40%', d: 'Fewer than four in ten third graders read proficiently in Delaware.' },
  { stat: '4×', d: 'Children not reading proficiently by third grade are four times more likely to leave school without a diploma.' },
  { stat: '$272K', d: 'Estimated lifetime public cost associated with each student who does not graduate.' },
];

const TIERS = [
  {
    id: 'visionary', title: 'Visionary', number: '$5,000+', color: '#b63435',
    short: 'Signals shared belief that universal statewide book ownership is achievable.',
    note: null,
    benefits: [
      'Recognition on website and launch materials',
      'Invitation to the annual statewide literacy briefing',
      'Eligibility for volunteer days and warehouse tours',
      'Inclusion in the annual Founders Circle impact report',
    ],
  },
  {
    id: 'catalyst', title: 'Catalyst', number: '$25,000+', color: '#14919f',
    short: 'Accelerate readiness and momentum toward a scalable foundation.',
    note: null,
    benefits: [
      'Priority invitations to warehouse previews and behind-the-scenes tours',
      'Quarterly operational and literacy impact updates',
      'Recognition in select distribution communications',
      'Volunteer and employee engagement opportunities',
      'All Visionary Founder tier benefits included',
    ],
  },
  {
    id: 'cornerstone', title: 'Cornerstone', number: '$100,000+', color: '#2c5570',
    short: 'Strengthen foundational systems for statewide distribution.',
    note: null,
    benefits: [
      'Naming opportunities for warehouse classrooms, training rooms, or workforce spaces',
      'Semiannual leadership dialogues on scale, cost, and systems building',
      'Alignment with workforce pathways or Public Benefit Corporation initiatives',
      'Dedicated liaison for partnership and visibility strategy',
      'All Catalyst Founder tier benefits included',
    ],
  },
  {
    id: 'transformational', title: 'Transformational', number: '$250,000+', color: '#b8860b',
    short: 'Anchor permanent statewide infrastructure for universal book ownership.',
    note: 'Many invest at $500,000 or more to underwrite major statewide milestones. Some invest $1 million or more, creating a lasting legacy for Delaware’s children.',
    benefits: [
      'Prominent naming opportunities for major statewide assets and initiatives',
      'Brand placement printed in or on books reaching households statewide',
      'Customized partnership and visibility strategy',
      'Annual strategic planning meeting with the CEO',
      'Recognition across statewide reports, events, and communications',
      'Priority access to grade-level and county-level adoption opportunities',
      'Opportunity to engage rising leaders through advisory cohorts',
      'All Cornerstone Founder tier benefits included',
    ],
  },
];

const SUSTAINING = [
  'Named recognition as a Sustaining Founder',
  'Early access to impact data, research findings, and statewide summaries',
  'Priority selection of grade-level adoption across Delaware',
  'Priority sponsorship of book categories (Financial Literacy, STEM, Wellness, Culture)',
  'Priority selection of Fall or Spring distribution cycles',
  'Naming stability for up to three years',
  'Eligibility for tasteful bookplates or nameplates tied to sponsored grades or categories',
  'Invitations to closed-door strategic briefings',
  'Priority access to classroom naming and high-visibility corporate engagement days',
];

const chevrons = (color) => <svg width="56" height="24" viewBox="0 0 76 30" fill="none" style={{ display: 'block', flex: 'none' }}><path d="M0 3 L9 3 L21 15 L9 27 L0 27 L12 15 Z" fill={color} opacity="0.4" /><path d="M23 3 L32 3 L44 15 L32 27 L23 27 L35 15 Z" fill={color} opacity="0.7" /><path d="M46 3 L55 3 L67 15 L55 27 L46 27 L58 15 Z" fill={color} opacity="1" /></svg>;
const Caret = ({ open }) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }}><polyline points="6 9 12 15 18 9" /></svg>;

/*
  P2 — Investors & Partners logo grid.
  Real assets confirmed in frontend/public/assets and renamed to semantic paths:
  bofa-horizontal.png, bofa-vertical.jpg, booksmiles-horizontal.png, booksmiles-badge.jpg.
  Horizontal versions are used as primary per spec; vertical/badge variants are on disk
  for future use but not referenced here.

  United Way of Delaware and Get Delaware Reading arrived as 960x540 JPEG slide exports: the mark sat in
  a large white field, so at the tile's fixed 48px logo height they would have rendered roughly a third
  the size of their neighbours. Both were trimmed to their content box and saved as PNG
  (logo-united-way.png 679x224, logo-get-delaware-reading.png 633x380) so they match the tight-cropped
  treatment of the two originals. The untrimmed .jpg source files stay on disk.
*/
const INVESTOR_LOGOS = [
  { name: 'Bank of America', sub: 'Client Foundation', logoSrc: '/assets/bofa-horizontal.png', alt: 'Bank of America logo' },
  { name: 'United Way of Delaware', sub: null, logoSrc: '/assets/logo-united-way.png', alt: 'United Way of Delaware logo' },
];
const PARTNER_LOGOS = [
  { name: 'BookSmiles', sub: null, logoSrc: '/assets/booksmiles-horizontal.png', alt: 'BookSmiles logo' },
  { name: 'Get Delaware Reading', sub: null, logoSrc: '/assets/logo-get-delaware-reading.png', alt: 'Get Delaware Reading logo' },
];

function LogoTile({ name, sub, logoSrc, alt }) {
  return (
    <div style={{ flex: '0 1 220px', maxWidth: 240, background: '#fff', border: '1px solid #e3e7ec', borderRadius: 16, boxShadow: '0 6px 20px rgba(18,50,74,.07)', padding: '26px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 148 }}>
      {logoSrc ? (
        <div style={{ width: '100%', maxWidth: 170, height: 48, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={logoSrc} alt={alt} style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }} />
        </div>
      ) : (
        <div style={{ width: '100%', minHeight: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <span style={rubik({ fontWeight: 700, fontSize: 18, color: '#2c5570', letterSpacing: '-.01em' })}>{name}</span>
        </div>
      )}
      {sub && <div style={{ font: "600 12.5px/1.2 'DM Sans'", letterSpacing: '.04em', textTransform: 'uppercase', color: '#7a8792' }}>{sub}</div>}
    </div>
  );
}

function LogoGroup({ heading, items, headingSize = 26 }) {
  return (
    <div>
      <h2 style={{ fontWeight: 700, fontSize: headingSize, letterSpacing: '-.01em', color: '#12324a', margin: '0 0 24px', textAlign: 'center' }}>{heading}</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
        {items.map((it) => <LogoTile key={it.name} {...it} />)}
      </div>
    </div>
  );
}

export default function FoundersCircle() {
  const [benefitsOpen, setBenefitsOpen] = useState(false);
  const [sustainOpen, setSustainOpen] = useState(false);

  return (
    <div className="thlp-founders" style={{ fontFamily: "'DM Sans',system-ui,sans-serif", color: '#25323d', background: '#fff' }}>

      {/* NAV + HERO */}
      {/* alignContent centres the copy in the band's minHeight instead of pinning it to the
          top and letting all the slack pool underneath (was 49px above, 187px below). Grid
          rather than flex so the single in-flow child still stretches full width and its
          `maxWidth + margin auto` keeps behaving exactly as it did in block layout. */}
      <div style={{ position: 'relative', minHeight: 480, background: '#10263a', display: 'grid', alignContent: 'center', gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <img src="/assets/hero-steps.png" alt="A child reading on the front steps" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 35%', filter: 'grayscale(1)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(16,38,58,.95) 0%, rgba(18,50,74,.86) 46%, rgba(20,145,159,.48) 100%)' }} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1240, margin: '0 auto', width: '100%' }}>
          <div style={{ padding: '48px 48px', maxWidth: 780 }}>
            <span style={eyebrow('#fad53a', '#10263a')}>Philanthropic Founders Circle</span>
            <h1 style={{ fontWeight: 900, fontSize: 56, lineHeight: 1, letterSpacing: '-.025em', color: '#fff', margin: '24px 0 0' }}>Building THLP Requires Visionary Leaders</h1>
            <p style={{ fontSize: 19, lineHeight: 1.6, color: 'rgba(255,255,255,.85)', margin: '22px 0 0', maxWidth: 600 }}>Together, public and private investment will make Delaware the first state where every child grows up with books at home.</p>
          </div>
        </div>
      </div>

      {/* PARTNERING FOR IMPACT
          The strip is a full-bleed separator and must butt straight onto the dark band, so
          this wrapper carries no bottom padding. The breathing room belongs ABOVE the strip:
          the content block's bottom padding matches its own 92px top padding, so the text
          sits equally clear of the section's top edge and of the strip below it. */}
      <div style={{ background: '#fff' }}>
        <div style={{ padding: '92px 0' }}>
        <div className="founders-partnering-grid" style={{ maxWidth: 1240, margin: '0 auto', padding: '0 48px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 56, alignItems: 'start' }}>
          <div style={rise('entry 0% cover 24%')}>
            <span style={eyebrow(AC, '#fff')}>Partnership Pathways</span>
            <h2 style={{ fontWeight: 700, fontSize: 38, lineHeight: 1.08, letterSpacing: '-.02em', color: '#12324a', margin: '18px 0 0' }}>Partnering for Impact</h2>
            <p style={{ fontSize: 16.5, lineHeight: 1.65, color: '#4a5560', margin: '18px 0 0' }}>The Home Library Project is built on a simple truth: lasting change requires shared commitment. When Delaware’s public, private, and philanthropic leaders invest in universal book ownership, they will establish books as foundational infrastructure, ensuring every child grows up surrounded by stories, knowledge, and possibility.</p>
            <p style={{ fontSize: 16.5, lineHeight: 1.65, color: '#4a5560', margin: '16px 0 0' }}>When these sectors align, aspiration becomes permanence. Proven ideas become transformative systems.</p>
            <div style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontWeight: 800, fontSize: 18, color: '#12324a', maxWidth: 360 }}>This is how shared commitment builds a legacy and becomes a lasting public good.</span>
              {chevrons(AC)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {PATHWAYS.map((p, i) => (
              <div key={p.t} style={{ padding: i === 0 ? '0 0 18px' : (i === PATHWAYS.length - 1 ? '18px 0 0' : '18px 0'), borderBottom: i === PATHWAYS.length - 1 ? 'none' : '1px solid rgba(44,85,112,.28)', ...rise(`entry ${2 + i * 2}% cover ${24 + i * 2}%`) }}>
                <div style={{ fontWeight: 700, fontSize: 19, color: '#12324a' }}>{p.t}</div>
                <p style={{ font: "400 14.5px/1.55 'DM Sans'", color: '#54606b', margin: '6px 0 0' }}>{p.d}</p>
              </div>
            ))}
          </div>
        </div>
        </div>
        <div style={{ position: 'relative', ...rise('entry 10% cover 34%') }}>
          <img src="/founders-headshots-color.jpg" alt="Diverse community members supporting Delaware's children" style={{ display: 'block', width: '100%', height: 'clamp(180px, 26vw, 280px)', objectFit: 'cover', objectPosition: 'center', borderRadius: 0 }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(44,85,112,.7), rgba(20,145,159,.5))', pointerEvents: 'none' }} />
        </div>
      </div>

      {/* SPONSORSHIP — dark band. As white cards at the tail of the white Partnering section
          these four read as a continuation of the pathways list rather than their own offer.
          Dark reuses the ink + teal-glow treatment of the Founders Circle section below, so the
          two "here is what you get" bands are visibly the same family. Tiles follow the home
          page .value-tile pattern (translucent fill, teal-tint icon) instead of white slabs. */}
      <div style={{ background: '#10263a', padding: '92px 0', backgroundImage: 'radial-gradient(circle at 12% 0%, rgba(20,145,159,.20), transparent 52%)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 48px', ...rise('entry 4% cover 28%') }}>
          {/* Heading and lede share one centred axis. Both were left-aligned but capped at
              different widths (760 vs 860), so they read as two different alignments. */}
          <h2 style={rubik({ fontWeight: 700, fontSize: 'clamp(28px,3.2vw,36px)', lineHeight: 1.12, letterSpacing: '-.02em', color: '#fff', margin: '0 auto', maxWidth: 760, textAlign: 'center' })}>Here are a few ways we can tell your story of involvement:</h2>
          <p style={{ fontSize: 16.5, lineHeight: 1.65, color: 'rgba(255,255,255,.82)', maxWidth: 860, margin: '18px auto 0', textAlign: 'center' }}>As a Founders Circle partner, you can bring your leadership to life by aligning it with the priorities and communities you care about most, from literacy and STEM to financial readiness, or by supporting specific geographies or grade levels.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 20, marginTop: 40 }}>
            {SPONSORSHIP.map((s, i) => (
              <div key={s.t} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.14)', borderRadius: 16, padding: '26px 28px', ...rise(`entry ${8 + i * 2}% cover ${32 + i * 2}%`) }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: 14, background: 'rgba(20,145,159,.22)', border: '1px solid rgba(127,208,217,.4)', marginBottom: 16 }}>
                  <s.Icon size={26} color="#7fd0d9" />
                </span>
                <div style={{ fontWeight: 700, fontSize: 18.5, color: '#fff', fontFamily: "'Rubik', sans-serif" }}>{s.t}</div>
                <p style={{ font: "400 14.5px/1.55 'DM Sans'", color: 'rgba(255,255,255,.75)', margin: '8px 0 0' }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* THE URGENCY (kids collage faintly behind; plain numbers) */}
      <div style={{ position: 'relative', background: '#eef1f4', padding: '92px 48px', overflow: 'hidden' }}>
        <img src="/assets/kids-grid.png" alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.18, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(238,241,244,.72), rgba(238,241,244,.88))' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48, ...rise('entry 0% cover 24%') }}>
            <span style={eyebrow('#b63435', '#fff')}>The Urgency</span>
            <h2 style={{ fontWeight: 700, fontSize: 40, lineHeight: 1.12, letterSpacing: '-.02em', color: '#12324a', margin: '18px 0 0' }}>Every Year We Wait,<br />Another Generation Falls Behind</h2>
            <p style={{ fontSize: 16.5, lineHeight: 1.7, color: '#4a5560', margin: '16px auto 0', maxWidth: 680 }}>Delaware is facing a literacy crisis. More than 60% of children from low-income households have no books at home. Low literacy is not just an educational challenge; it is a long-term economic and community issue that affects all of us.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 36 }}>
            {URGENCY.map((u, i) => (
              <div key={u.stat} style={{ textAlign: 'center', ...rise(`entry ${4 + i * 3}% cover ${26 + i * 3}%`) }}>
                <div style={rubik({ fontWeight: 900, fontSize: 60, letterSpacing: '-.02em', color: '#b63435', lineHeight: 1 })}>{u.stat}</div>
                <p style={{ fontSize: 14.5, lineHeight: 1.55, color: '#54606b', margin: '14px auto 0', maxWidth: 280 }}>{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CHANGE WON'T HAPPEN WITHOUT YOU — restored bridge section, rebuilt in current design system */}
      <div style={{ background: '#2c5570', padding: '76px 48px', textAlign: 'center' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', ...rise('entry 0% cover 26%') }}>
          <h2 style={rubik({ fontWeight: 800, fontStyle: 'italic', fontSize: 'clamp(30px,4vw,38px)', lineHeight: 1.15, letterSpacing: '-.01em', color: '#fff', margin: 0 })}>Change Won’t Happen Without You</h2>
          <p style={{ fontSize: 17, lineHeight: 1.65, color: 'rgba(255,255,255,.9)', margin: '20px 0 0' }}>Building the first universal, statewide book ownership model in the nation requires infrastructure, workforce, logistics, and systems that don’t yet exist. <strong>Founders Circle partners</strong> are the visionaries making it possible, brick by brick, book by book.</p>
        </div>
      </div>

      {/* JOIN THE FOUNDERS CIRCLE (dark): tiers (all expanded) + sustaining */}
      <div style={{ position: 'relative', background: '#10263a', padding: '92px 0', backgroundImage: 'radial-gradient(circle at 90% 8%, rgba(20,145,159,.18), transparent 44%)' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ textAlign: 'center', marginBottom: 14, ...rise('entry 0% cover 22%') }}>
            <span style={eyebrow('#fad53a', '#10263a')}>Founders Circle</span>
            <h2 style={{ fontWeight: 700, fontSize: 40, letterSpacing: '-.02em', color: '#fff', margin: '18px 0 0' }}>Join the Founders Circle</h2>
            <p style={{ fontSize: 16.5, lineHeight: 1.6, color: 'rgba(255,255,255,.8)', margin: '14px auto 0', maxWidth: 680 }}>An annual investment of $3 to $6 million will establish book ownership as public infrastructure across Delaware. Every partner contributing $5,000 or more joins this inaugural group.</p>
          </div>

          {/* Tier cards — number block on top, title above the body copy, all benefits shown */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: 20, marginTop: 44 }}>
            {TIERS.map((t, i) => (
              <div key={t.id} style={{ display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 18, overflow: 'hidden', boxShadow: '0 16px 44px rgba(0,0,0,.30)', ...rise(`entry ${4 + i * 2}% cover ${26 + i * 2}%`) }}>
                <div style={{ padding: '14px 14px 0' }}>
                  <div style={{ background: t.color, borderRadius: 14, padding: '22px 14px', textAlign: 'center' }}>
                    <div style={rubik({ fontWeight: 900, fontSize: 34, letterSpacing: '-.02em', color: '#fff', lineHeight: 1 })}>{t.number}</div>
                  </div>
                </div>
                <div style={{ padding: '20px 22px 26px', textAlign: 'center', display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div style={{ font: "800 14px/1 'Rubik'", letterSpacing: '.1em', textTransform: 'uppercase', color: t.color, marginBottom: 12 }}>{t.title}</div>
                  <p style={{ font: "400 13.5px/1.55 'DM Sans'", color: '#54606b', margin: 0 }}>{t.short}</p>
                  {benefitsOpen && (
                    <>
                      {t.note && <p style={{ font: "400 12.5px/1.5 'DM Sans'", color: '#8894a0', fontStyle: 'italic', margin: '14px 0 0' }}>{t.note}</p>}
                      <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {t.benefits.map((bft) => (
                          <li key={bft} style={{ display: 'flex', gap: 9, font: "400 13px/1.5 'DM Sans'", color: '#54606b' }}>
                            <span style={{ flex: 'none', width: 6, height: 6, borderRadius: 999, background: t.color, marginTop: 6 }} />{bft}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: 30 }}>
            <button type="button" onClick={() => setBenefitsOpen(!benefitsOpen)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 26px', borderRadius: 10, border: '1px solid rgba(255,255,255,.35)', background: benefitsOpen ? 'rgba(255,255,255,.08)' : 'transparent', color: '#fff', font: "700 14px/1 'DM Sans'", cursor: 'pointer' }}
              data-testid="benefits-toggle" aria-expanded={benefitsOpen}>
              {benefitsOpen ? 'Hide Benefits' : 'View Benefits for Every Tier'} <Caret open={benefitsOpen} />
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: 44 }}>
            <Link to="/contact?interest=founders-circle" style={{ display: 'inline-flex', padding: '16px 34px', borderRadius: 12, background: '#fad53a', color: '#10263a', font: "700 15.5px/1 'DM Sans'", boxShadow: '0 8px 24px rgba(240,180,40,.3)', textDecoration: 'none' }} data-testid="founders-cta">Become a Founding Partner</Link>
            <div style={{ marginTop: 16 }}>
              <Link to="/donate" style={{ font: "600 14px/1 'DM Sans'", color: 'rgba(255,255,255,.75)', textDecoration: 'underline' }}>Prefer to give now? Donate</Link>
            </div>
          </div>

          {/* Sustaining Founders — underneath, inside the dark section */}
          <div style={{ marginTop: 44, maxWidth: 940, marginLeft: 'auto', marginRight: 'auto', ...rise('entry 0% cover 30%') }}>
            <div style={{ background: '#132b3f', border: '1px solid rgba(255,255,255,.1)', borderRadius: 18, overflow: 'hidden' }}>
              <div style={{ height: 4, background: AC }} />
              <div style={{ padding: '28px 30px' }}>
                <h3 style={{ fontWeight: 700, fontSize: 24, letterSpacing: '-.01em', color: '#fff', margin: 0 }}>Sustaining Founders</h3>
                <p style={{ fontSize: 15.5, lineHeight: 1.65, color: 'rgba(255,255,255,.82)', margin: '10px 0 0' }}>Partners who commit to multi-year support anchor the model’s stability and unlock a deeper set of recognition and engagement benefits.</p>
                <button type="button" onClick={() => setSustainOpen(!sustainOpen)}
                  style={{ marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 20px', borderRadius: 10, border: `1px solid ${AC}`, background: sustainOpen ? AC : 'transparent', color: '#fff', font: "700 13.5px/1 'DM Sans'", cursor: 'pointer' }}
                  data-testid="sustaining-toggle" aria-expanded={sustainOpen}>
                  {sustainOpen ? 'Hide Sustaining Benefits' : 'View Sustaining Benefits'} <Caret open={sustainOpen} />
                </button>
                {sustainOpen && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '22px 0 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 12 }}>
                    {SUSTAINING.map((s) => (
                      <li key={s} style={{ display: 'flex', gap: 10, font: "400 14px/1.5 'DM Sans'", color: 'rgba(255,255,255,.85)' }}>
                        <span style={{ flex: 'none', width: 7, height: 7, borderRadius: 999, background: AC, marginTop: 7 }} />{s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* INVESTORS & PARTNERS (P2) — logo grid, grows as entries are added */}
      <div style={{ background: '#F6F7F9', padding: '92px 0' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ textAlign: 'center', marginBottom: 44, ...rise('entry 0% cover 24%') }}>
            <span style={eyebrow(AC, '#fff')}>Investors &amp; Partners</span>
          </div>
          <div style={{ ...rise('entry 2% cover 26%') }}>
            <LogoGroup heading="Investors in our Mission" items={INVESTOR_LOGOS} headingSize={28} />
          </div>
          <div style={{ height: 52 }} />
          <div style={{ ...rise('entry 4% cover 28%') }}>
            <LogoGroup heading="Partners" items={PARTNER_LOGOS} headingSize={26} />
          </div>
        </div>
      </div>

      {/* BE PART OF THE STORY — restored closing CTA section, rebuilt in current design system */}
      <div style={{ background: '#fff', padding: '92px 48px', textAlign: 'center' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', ...rise('entry 0% cover 26%') }}>
          <h2 style={{ fontWeight: 700, fontSize: 40, letterSpacing: '-.02em', color: '#12324a', margin: 0 }}>Be Part of the Story</h2>
          <p style={{ fontSize: 19.5, lineHeight: 1.65, color: '#4a5560', margin: '22px 0 0' }}>Founders don’t just give. They build. They shape. They create something that will change what it means to grow up in Delaware.</p>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: '#54606b', margin: '14px 0 0' }}>This is your invitation to be an architect of change.</p>
          <div style={{ marginTop: 34, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact?interest=founders-circle" style={{ display: 'inline-flex', padding: '16px 34px', borderRadius: 12, background: '#fad53a', color: '#10263a', font: "700 15.5px/1 'DM Sans'", boxShadow: '0 8px 24px rgba(240,180,40,.3)', textDecoration: 'none' }}>Join the Founders Circle</Link>
            <Link to="/contact" style={{ display: 'inline-flex', padding: '16px 34px', borderRadius: 12, background: 'transparent', border: '2px solid #2c5570', color: '#2c5570', font: "700 15.5px/1 'DM Sans'", textDecoration: 'none' }}>Schedule a Conversation</Link>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ background: '#0a1721' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '44px 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
          <Link to="/"><img src="/assets/logo-white.png" alt="The Home Library Project" style={{ height: 76, width: 'auto', display: 'block' }} /></Link>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 14, textAlign: 'right' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'flex-end' }}>
              {[['Home', '/'], ['About', '/about'], ['Research', '/research'], ['Contact', '/contact'], ['Donate', '/donate']].map(([l, t]) => <Link key={l} to={t} style={{ font: "600 14px/1 'DM Sans'", color: 'rgba(255,255,255,.85)', textDecoration: 'none' }}>{l}</Link>)}
            </div>
            <SocialLinks />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end', color: 'rgba(255,255,255,.5)', font: "400 13px/1.5 'DM Sans'" }}>
              <span style={{ color: 'rgba(255,255,255,.55)' }}>© 2026 The Home Library Project. 501(c)(3) Nonprofit Organization.</span>
              <Link to="/privacy" style={{ color: 'rgba(255,255,255,.5)', textDecoration: 'none' }}>Privacy Policy</Link>
              <Link to="/terms" style={{ color: 'rgba(255,255,255,.5)', textDecoration: 'none' }}>Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
